import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Custom Hook to track user activity status.
 * @param {number} timeoutInSeconds - The time (in seconds) of inactivity to wait before returning 'false'.
 * @returns {boolean} isActive - Returns TRUE if the user is currently active, and FALSE if inactive/expired.
 */
const useActivityStatus = (timeoutInSeconds) => {
  // State to track the activity status. Initial state is active (true).
  const [isActive, setIsActive] = useState(true);
  const timeoutRef = useRef(null);
  const timeoutSecondsRef = useRef(timeoutInSeconds);

  // Events to monitor for activity
  const events = [
    "load",
    "mousemove",
    "mousedown",
    "click",
    "scroll",
    "keypress",
  ];

  // Update ref when timeoutInSeconds changes
  useEffect(() => {
    timeoutSecondsRef.current = timeoutInSeconds;
  }, [timeoutInSeconds]);

  // Function to reset the timer and set state to active (true)
  const resetTimer = useCallback(() => {
    // 1. Set the user to ACTIVE
    setIsActive(true);

    // 2. Clear the existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // 3. Set a new timer with the current timeout value
    timeoutRef.current = setTimeout(() => {
      // 4. When the timer expires, set the user to INACTIVE (false)
      console.warn(
        `⏰ Inactivity timeout triggered after ${timeoutSecondsRef.current} seconds`,
      );
      setIsActive(false);
    }, timeoutSecondsRef.current * 1000); // Convert seconds to milliseconds
  }, []);

  // useEffect to register and clean up listeners
  useEffect(() => {
    // Start the initial timer
    resetTimer();

    // Attach listeners to reset the timer on any user activity
    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    // Cleanup: Remove listeners and clear the timer when the component unmounts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer]); // Dependency array

  // Return the activity status
  return isActive;
};

export default useActivityStatus;
